/**
 * LivePulseTicker — real-time community activity marquee for the homepage.
 *
 * Distinctive features:
 *   • Pulsing crimson "LIVE" badge with a beating dot + Activity icon.
 *   • Category filter chips (All · Nature · Weather · Volunteer · People · Orgs).
 *   • Alert-level items get a crimson glow + siren icon + are pinned front.
 *   • "You" contributions get a golden outline + "you" badge.
 *   • Optional chime on new items (localStorage-persisted per user).
 *   • Horizontal seamless marquee (duplicate content zero-gap loop).
 *   • Hover pauses the strip; fade edges on both sides.
 *   • ECG heartbeat baseline beneath the strip reinforces "live".
 *
 * Data source: /api/feed/live-pulse?limit=30&kind=<key> — polled every 45s.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Activity, Volume2, VolumeX } from "lucide-react";
import confetti from "canvas-confetti";

/* ─── relative time helper ────────────────────────────────────────── */
function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

/* ─── one pill ────────────────────────────────────────────────────── */
function PulseItem({ item }) {
  const alertStyle = item.is_alert
    ? {
        borderColor: "#dc2626",
        boxShadow: "0 0 0 2px rgba(220,38,38,0.15), 0 2px 6px rgba(220,38,38,0.25)",
      }
    : {};
  const mineStyle = item.is_mine
    ? {
        borderColor: "#d97706",
        boxShadow: "0 0 0 2px rgba(217,119,6,0.20)",
      }
    : {};
  return (
    <Link
      to={item.link || "/"}
      data-testid={`live-pulse-item-${item.kind}`}
      data-alert={item.is_alert ? "true" : "false"}
      data-mine={item.is_mine ? "true" : "false"}
      className="group inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 mx-2 text-xs whitespace-nowrap border transition-all hover:-translate-y-0.5 hover:shadow-md"
      style={{
        background: "var(--wofy-bg)",
        borderColor: "var(--wofy-border)",
        color: "var(--wofy-text)",
        ...alertStyle,
        ...mineStyle,
      }}
    >
      {item.is_alert && (
        <span
          className="text-[10px] font-mono font-bold uppercase text-white rounded-full px-1.5 py-0.5"
          style={{ background: "#dc2626" }}
        >
          🚨 Alert
        </span>
      )}
      {item.is_mine && (
        <span
          className="text-[10px] font-mono font-bold uppercase text-white rounded-full px-1.5 py-0.5"
          style={{ background: "#d97706" }}
        >
          ✨ You
        </span>
      )}
      <span
        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-white"
        style={{ background: item.chip_color || "#059669" }}
      >
        <span className="text-sm leading-none">{item.icon}</span>
        {item.chip}
      </span>
      <span className="font-medium">{item.text}</span>
      <span className="opacity-50 text-[10px] font-mono">· {relTime(item.timestamp)}</span>
    </Link>
  );
}

/* ─── chime helper (Web Audio, no asset dependency) ──────────────── */
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.4);
    setTimeout(() => ctx.close(), 600);
  } catch (_) { /* silent */ }
}

/* ─── confetti burst when a user's own pulse arrives ────────────── */
function fireContributorConfetti() {
  try {
    const defaults = { spread: 70, startVelocity: 35, ticks: 60, zIndex: 60 };
    confetti({ ...defaults, particleCount: 40, origin: { x: 0.1, y: 0.9 }, colors: ["#d97706","#f59e0b","#fbbf24","#059669","#0ea5e9"] });
    setTimeout(() => confetti({ ...defaults, particleCount: 40, origin: { x: 0.9, y: 0.9 }, colors: ["#d97706","#f59e0b","#fbbf24","#059669","#0ea5e9"] }), 180);
  } catch (_) { /* silent */ }
}

/* ─── main ticker ─────────────────────────────────────────────────── */
const KIND_ALL = "all";
const SOUND_KEY = "wofy.livePulse.sound";

// Where each chip should take the user when tapped. Keys mirror the
// `kind` values returned by /api/feed/live-pulse. Anything not in the
// map (or `all`) falls back to a filter-only chip.
const KIND_ROUTES = {
  nature:              "/nature",
  weather:             "/weather",
  volunteer:           "/volunteer",
  popular_people:      "/members",
  members:             "/members",
  people:              "/members",
  organizations:       "/organizations",
  orgs:                "/organizations",
  impact_shared:       "/impact-map",
  causes:              "/causes",
  jobs:                "/jobs",
  news:                "/things-to-know",
  events:              "/events",
  marketplace:         "/marketplace",
};

export default function LivePulseTicker() {
  const [items, setItems]   = useState([]);
  const [kinds, setKinds]   = useState([]);
  const [filter, setFilter] = useState(KIND_ALL);
  const [paused, setPaused] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem(SOUND_KEY) === "1"; } catch { return false; }
  });
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = () => {
    http
      .get(`/feed/live-pulse?limit=30&kind=${filter}`)
      .then((r) => {
        const next = r.data?.items || [];
        setItems(next);
        if (!kinds.length && Array.isArray(r.data?.kinds)) setKinds(r.data.kinds);

        const seen = seenIdsRef.current;
        const newItems = next.filter((it) => !seen.has(it.id));

        if (!firstLoadRef.current && newItems.length > 0) {
          // Chime for any new item
          if (soundOn) playChime();
          // Confetti burst when the caller sees their OWN pulse land
          if (newItems.some((it) => it.is_mine)) {
            fireContributorConfetti();
          }
        }

        seenIdsRef.current = new Set(next.map((it) => it.id));
        firstLoadRef.current = false;
      })
      .catch(() => {});
  };

  useEffect(() => {
    // Reset seen-tracking when the filter changes so we don't chime for
    // items that were merely hidden by the previous filter.
    seenIdsRef.current = new Set();
    firstLoadRef.current = true;
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, soundOn]);

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      try { localStorage.setItem(SOUND_KEY, next ? "1" : "0"); } catch {}
      if (next) playChime();   // audible confirmation
      return next;
    });
  };

  const chipList = useMemo(() => (
    [{ key: KIND_ALL, label: "All", color: "#525252" }, ...kinds]
  ), [kinds]);

  if (!items.length) return null;
  const strip = [...items, ...items];  // seamless loop

  return (
    <section
      data-testid="live-pulse-ticker"
      className="relative border-y overflow-hidden"
      style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{`
        @keyframes lp-marquee { from { transform: translateX(0);} to { transform: translateX(-50%);} }
        @keyframes lp-beat   { 0%,100% { transform: scale(1); opacity: 1;} 50% { transform: scale(1.6); opacity: .4;} }
        @keyframes lp-ecg    { 0%{stroke-dashoffset:800;} 100%{stroke-dashoffset:0;} }
        .lp-rail { animation: lp-marquee 90s linear infinite; will-change: transform;}
        .lp-rail[data-paused="true"] { animation-play-state: paused;}
      `}</style>

      {/* Row 1 — filter chips + sound toggle */}
      <div
        className="max-w-[1600px] mx-auto flex items-center gap-2 px-6 lg:px-10 py-1.5 border-b"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <div className="flex items-center gap-2 shrink-0 mr-2">
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: "#dc2626", animation: "lp-beat 1.1s ease-in-out infinite" }}
            aria-hidden
          />
          <span className="text-[11px] font-mono uppercase tracking-widest font-semibold" style={{ color: "#dc2626" }}>
            Live · Pulse
          </span>
          <Activity className="w-3.5 h-3.5" style={{ color: "var(--wofy-text-soft)" }} />
        </div>

        <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto pr-2">
          {chipList.map((k) => {
            const active = filter === k.key;
            const route = KIND_ROUTES[k.key];
            const chipCls =
              "text-[11px] font-mono uppercase tracking-wider rounded-full px-2.5 py-0.5 border whitespace-nowrap transition-all";
            const chipStyle = {
              background:  active ? k.color : "transparent",
              color:       active ? "#fff"   : "var(--wofy-text-soft)",
              borderColor: active ? k.color : "var(--wofy-border)",
            };

            // "All" and any kind without a route → filter-only button.
            // Kinds with a mapped route → navigate to that section so the
            // ticker doubles as a mobile nav (tap Nature → /nature) while
            // still setting the filter for anyone who stays on the page.
            if (!route || k.key === KIND_ALL) {
              return (
                <button
                  key={k.key}
                  data-testid={`live-pulse-filter-${k.key}`}
                  onClick={() => setFilter(k.key)}
                  className={chipCls}
                  style={chipStyle}
                >
                  {k.label}
                </button>
              );
            }
            return (
              <Link
                key={k.key}
                to={route}
                onClick={() => setFilter(k.key)}
                data-testid={`live-pulse-filter-${k.key}`}
                className={chipCls}
                style={chipStyle}
              >
                {k.label}
              </Link>
            );
          })}
        </div>

        <button
          data-testid="live-pulse-sound-toggle"
          onClick={toggleSound}
          title={soundOn ? "Mute new-pulse chime" : "Play a soft chime on new pulses"}
          aria-label={soundOn ? "Mute new-pulse chime" : "Play new-pulse chime"}
          className="hidden sm:inline-flex shrink-0 rounded-full p-1.5 border transition-colors items-center justify-center"
          style={{
            borderColor: "var(--wofy-border)",
            background: soundOn ? "#fef3c7" : "transparent",
            color: soundOn ? "#b45309" : "var(--wofy-text-soft)",
          }}
        >
          {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Row 2 — the marquee rail */}
      <div className="max-w-[1600px] mx-auto flex items-stretch">
        <div className="relative flex-1 overflow-hidden py-2">
          <div className="lp-rail flex items-center" data-paused={paused} style={{ width: "max-content" }}>
            {strip.map((it, idx) => (
              <PulseItem key={`${it.id}-${idx}`} item={it} />
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12"
               style={{ background: "linear-gradient(90deg, var(--wofy-surface), transparent)" }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12"
               style={{ background: "linear-gradient(-90deg, var(--wofy-surface), transparent)" }} />
        </div>
      </div>

      {/* Row 3 — ECG heartbeat baseline */}
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 pb-1.5" aria-hidden>
        <svg viewBox="0 0 800 12" preserveAspectRatio="none" className="w-full h-2 opacity-60">
          <path
            d="M0 6 L120 6 L128 2 L136 10 L144 4 L152 8 L160 6 L320 6 L328 1 L336 11 L344 3 L352 9 L360 6 L800 6"
            fill="none" stroke="#dc2626" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"
            style={{ strokeDasharray: 800, animation: "lp-ecg 6s linear infinite" }}
          />
        </svg>
      </div>
    </section>
  );
}
